/**
 * A hire request's business case: why the hire is needed, and HR's Selection
 * Approach template (20 Oct 2026), the rows the hiring manager and HR agree
 * before anything is advertised. The role and the appointment type are asked
 * for elsewhere on the request, so they are not repeated here.
 *
 * The labels, the form's draft and its checks live here so the request page,
 * HR Ops' Position requests and Role Master's Requests tab say the same thing.
 * identity-service checks the same rules (services/hire_requests.py).
 */
import type {
  AdvertPlatform,
  BasedAt,
  InterviewGuide,
  SelectionApproach,
  Sourcing,
  WorkMode,
} from '../api/services/hireRequestService';

export const MIN_WHY = 20;
export const MIN_SELECTION_TEXT = 10;
export const MIN_PANEL = 3;
export const MAX_ADVERT_DAYS = 90;
export const MAX_SALARY = 10_000_000;

type Option<T> = { value: T; label: string };

export const SOURCING: Option<Sourcing>[] = [
  { value: 'internal_only', label: 'Internal only' },
  { value: 'internal_first', label: 'Internal first' },
  { value: 'internal_and_external', label: 'Internal and external' },
  { value: 'external_only', label: 'External only' },
];

/** In the template's order, which is also the order they are stored in. */
export const PLATFORMS: Option<AdvertPlatform>[] = [
  { value: 'eduvos', label: "Eduvos's own channels" },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'pnet', label: 'PNet' },
  { value: 'careers24', label: 'Careers24' },
  { value: 'other', label: 'Other' },
];

export const INTERVIEW_GUIDES: Option<InterviewGuide>[] = [
  { value: 'standard', label: 'Standard interview guide' },
  { value: 'role_specific', label: 'Questions for this role' },
];

export const BASED: Option<BasedAt>[] = [
  { value: 'campus', label: 'Only at the campus asked for' },
  { value: 'any_campus', label: 'Any campus' },
  { value: 'national', label: 'National role' },
];

export const WORK_MODES: Option<WorkMode>[] = [
  { value: 'onsite', label: 'On site' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'remote', label: 'Remote' },
];

const labelOf = <T extends string>(options: Option<T>[], value: T | null | undefined): string =>
  options.find((o) => o.value === value)?.label || String(value || '');

/** "Only at PT" once the campus is known. */
export const basedLabel = (value: BasedAt | null | undefined, campus?: string | null): string =>
  value === 'campus' && campus ? `Only at ${campus}` : labelOf(BASED, value);

/** The form's answers as typed: numbers stay text until they are sent. */
export interface BusinessCaseDraft {
  why: string;
  selection_criteria: string;
  salary_from: string;
  salary_to: string;
  salary_per: SelectionApproach['salary_per'];
  sourcing: Sourcing | '';
  platforms: AdvertPlatform[];
  platforms_other: string;
  advert_days: string;
  check_ins: string;
  case_study: 'yes' | 'no' | '';
  case_study_detail: string;
  interview_guide: InterviewGuide | '';
  interview_guide_detail: string;
  panel: string;
  based: BasedAt | '';
  work_mode: WorkMode | '';
  anything_else: string;
}

export const emptyBusinessCase = (): BusinessCaseDraft => ({
  why: '',
  selection_criteria: '',
  salary_from: '',
  salary_to: '',
  salary_per: 'month',
  sourcing: '',
  platforms: [],
  platforms_other: '',
  advert_days: '',
  check_ins: '',
  case_study: '',
  case_study_detail: '',
  interview_guide: '',
  interview_guide_detail: '',
  panel: '',
  based: '',
  work_mode: '',
  anything_else: '',
});

/** Text as the server measures it: trimmed, inner runs of spaces collapsed. */
export const words = (s: string | null | undefined): string => (s || '').trim().replace(/\s+/g, ' ');

/** A whole number typed into a field, or null. */
const whole = (s: string): number | null => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);

const rand = (s: string): number | null => {
  const n = whole(s);
  return n !== null && n >= 1 && n <= MAX_SALARY ? n : null;
};

/** What is still missing, one plain sentence per question; empty when the
 *  business case can be sent. */
export function businessCaseProblems(d: BusinessCaseDraft): string[] {
  const out: string[] = [];
  if (words(d.why).length < MIN_WHY) out.push(`Say why this hire is needed (at least ${MIN_WHY} characters).`);
  if (words(d.selection_criteria).length < MIN_SELECTION_TEXT) {
    out.push('Give the selection criteria: the qualifications, experience and skills the role needs.');
  }
  const from = rand(d.salary_from);
  const to = rand(d.salary_to);
  if (from === null || to === null) out.push('Give the salary range, in whole rands.');
  else if (to < from) out.push("The top of the salary range can't be below the bottom.");
  if (!d.sourcing) out.push('Choose how the role is sourced: internal, external or both.');
  if (!d.platforms.length) out.push('Choose where the role is advertised.');
  else if (d.platforms.includes('other') && !words(d.platforms_other)) {
    out.push('Say which other place the role is advertised on.');
  }
  const days = whole(d.advert_days);
  if (days === null || days < 1 || days > MAX_ADVERT_DAYS) {
    out.push(`Say how many days the advert runs (1 to ${MAX_ADVERT_DAYS}).`);
  }
  if (!d.case_study) out.push('Say whether candidates do a case study or task.');
  else if (d.case_study === 'yes' && words(d.case_study_detail).length < MIN_SELECTION_TEXT) {
    out.push('Describe the case study or task: what it is, when it is used and who assesses it.');
  }
  if (!d.interview_guide) out.push('Choose the interview guide.');
  else if (d.interview_guide === 'role_specific' && words(d.interview_guide_detail).length < MIN_SELECTION_TEXT) {
    out.push('Give the technical questions and competencies the interview covers.');
  }
  if (words(d.panel).length < MIN_PANEL) out.push('Say who is on the interview panel.');
  if (!d.based) out.push('Say where the person can be based.');
  if (!d.work_mode) out.push('Choose on site, hybrid or remote.');
  return out;
}

/** The selection approach as it is sent, once `businessCaseProblems` is
 *  empty. Text keeps its line breaks; the server tidies the rest. */
export function toSelectionApproach(d: BusinessCaseDraft): SelectionApproach {
  const caseStudy = d.case_study === 'yes';
  const roleSpecific = d.interview_guide === 'role_specific';
  const other = d.platforms.includes('other');
  return {
    selection_criteria: d.selection_criteria.trim(),
    salary_from: Number(d.salary_from.trim()),
    salary_to: Number(d.salary_to.trim()),
    salary_per: d.salary_per,
    sourcing: d.sourcing as Sourcing,
    platforms: PLATFORMS.map((p) => p.value).filter((v) => d.platforms.includes(v)),
    platforms_other: other ? words(d.platforms_other) : null,
    advert_days: Number(d.advert_days.trim()),
    check_ins: words(d.check_ins) || null,
    case_study: caseStudy,
    case_study_detail: caseStudy ? d.case_study_detail.trim() : null,
    interview_guide: d.interview_guide as InterviewGuide,
    interview_guide_detail: roleSpecific ? d.interview_guide_detail.trim() : null,
    panel: words(d.panel),
    based: d.based as BasedAt,
    work_mode: d.work_mode as WorkMode,
    anything_else: d.anything_else.trim() || null,
  };
}

export const formatRands = (n: number): string =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(n);

/** How often the salary is paid, in words: a contractor's rate is by the hour. */
export const SALARY_PER: Array<{ value: SelectionApproach['salary_per']; label: string }> = [
  { value: 'month', label: 'a month' },
  { value: 'year', label: 'a year' },
  { value: 'hour', label: 'an hour' },
];

/** "R28 000 to R30 000 a month", or one figure when both ends are the same. */
export const salaryLabel = (sa: Pick<SelectionApproach, 'salary_from' | 'salary_to' | 'salary_per'>): string => {
  const per = SALARY_PER.find((p) => p.value === sa.salary_per)?.label || 'a month';
  return sa.salary_from === sa.salary_to
    ? `${formatRands(sa.salary_from)} ${per}`
    : `${formatRands(sa.salary_from)} to ${formatRands(sa.salary_to)} ${per}`;
};

export interface BusinessCaseSection {
  title: string;
  rows: Array<{ label: string; value: string }>;
}

/** The business case in the template's order, for reading. A request raised
 *  before the template has its why alone. */
export function describeBusinessCase(
  why: string | null | undefined,
  sa: SelectionApproach | null | undefined,
  campus?: string | null,
): BusinessCaseSection[] {
  const sections: BusinessCaseSection[] = [];
  const role: BusinessCaseSection['rows'] = [];
  if (words(why)) role.push({ label: 'Why this hire is needed', value: (why || '').trim() });
  if (sa) {
    role.push({ label: 'Selection criteria', value: sa.selection_criteria });
    role.push({ label: 'Salary range', value: salaryLabel(sa) });
  }
  if (role.length) sections.push({ title: 'The role', rows: role });
  if (!sa) return sections;

  const where = (sa.platforms || []).map((p) => (
    p === 'other' && sa.platforms_other ? `Other: ${sa.platforms_other}` : labelOf(PLATFORMS, p)));
  sections.push({
    title: 'Sourcing',
    rows: [
      { label: 'Internal or external', value: labelOf(SOURCING, sa.sourcing) },
      { label: 'Advertised on', value: where.join(', ') },
      { label: 'Advert runs for', value: `${sa.advert_days} day${sa.advert_days === 1 ? '' : 's'}` },
      ...(sa.check_ins ? [{ label: 'Check-ins with HR', value: sa.check_ins }] : []),
    ],
  });
  sections.push({
    title: 'Selection',
    rows: [
      { label: 'Case study or task', value: sa.case_study ? `Yes: ${sa.case_study_detail || ''}`.trim() : 'No' },
      {
        label: 'Interview guide',
        value: sa.interview_guide === 'role_specific' && sa.interview_guide_detail
          ? `${labelOf(INTERVIEW_GUIDES, sa.interview_guide)}: ${sa.interview_guide_detail}`
          : labelOf(INTERVIEW_GUIDES, sa.interview_guide),
      },
      { label: 'Interview panel', value: sa.panel },
    ],
  });
  sections.push({
    title: 'Location',
    rows: [
      { label: 'Where the person is based', value: basedLabel(sa.based, campus) },
      { label: 'Way of working', value: labelOf(WORK_MODES, sa.work_mode) },
    ],
  });
  // A row without a label reads as a paragraph.
  if (sa.anything_else) sections.push({ title: 'Anything else', rows: [{ label: '', value: sa.anything_else }] });
  return sections;
}
